"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useMemo } from "react";
import { Fragment } from "react";
import styles from "./ComparisonPage.module.scss";
import { COMPARISON_CATEGORY_ORDER } from "@constants/comparison";
import CompareCard from "../CompareCard";
import assetLoader, { s3UrlLoader } from "@utils/internalImageLoder";
import { toKebabCase } from "@utils/stringFormatters";
import ToggleSwitch from "@components/atoms/ToggleSwitch";
import { ComparisonData, IModelsData } from "@interfaces/comparison.types";
import AccentBar from "@components/atoms/AccentBar";
import { useIsMobile } from "@hooks/use-mobile";
import {
    addModelToComparisonSlug,
    extractCarSlugs,
    getMakeModelPairs,
    updateComparisonSlug,
} from "@utils/comparison";
import ModalManager from "@components/molecules/ModalManager";
import Button from "@components/atoms/Button";
import AddToCompareModal from "@components/molecules/MMVSelectModal";
import ComparePriceCard from "@components/molecules/ComparePriceCard";
import { CarVariant } from "../../molecules/ComparisonVariantDetails";
import { DEFAULT_CITY } from "@constants/global";
import ImageAtom from "@components/atoms/Image";
import { updateAppBar } from "@hooks/useWebviewCallback";
import { getWishlistAppbarConfig } from "@utils/appbarConfig";
import { useGlobalLoader } from "@hooks/useGlobalLoader";
interface Props {
    data: ComparisonData[];
    slug: string;
    city: any;
    isLoggedIn: boolean;
    modelsDetails?: IModelsData[];
    isApp: boolean
}

const MAX_COMPARISON_CARDS = 3;
const REMOVED_INDEXES_PARAM = "removedIndexes";
const LEGACY_REMOVED_MODELS_PARAM = "removedModels";

const parseRemovedIndexes = (value: string) =>
    new Set(
        value
            .split(",")
            .map((index) => Number(index))
            .filter((index) => Number.isInteger(index) && index >= 0)
    );

const parseLegacyRemovedIndexes = (value: string, modelSlugs: string[]) => {
    const removedModels = new Set(
        value.split(",").map((modelSlug) => modelSlug.trim()).filter(Boolean)
    );

    return new Set(
        modelSlugs
            .map((modelSlug, index) => removedModels.has(modelSlug) ? index : -1)
            .filter((index) => index >= 0)
    );
};

const buildCompareUrlWithRemovedIndexes = (
    items: { modelSlug: string; variantSlug?: string | null }[],
    removedIndexes: Set<number>,
    basePath = "/compare"
) => {
    const compareItems = items
        .map((item, originalIndex) => ({ ...item, originalIndex }))
        .filter((item) => Boolean(item.modelSlug))
        .sort((first, second) =>
            first.modelSlug.localeCompare(second.modelSlug) ||
            first.originalIndex - second.originalIndex
        );
    const modelSegment = compareItems.map((item) => item.modelSlug).join("-vs-");
    const queryParams = new URLSearchParams();

    compareItems.forEach((item, index) => {
        if (item.variantSlug && !removedIndexes.has(item.originalIndex)) {
            queryParams.set(`variant${index + 1}`, item.variantSlug);
        }
    });

    const sortedRemovedIndexes = compareItems
        .map((item, index) => removedIndexes.has(item.originalIndex) ? index : -1)
        .filter((index) => index >= 0);
    if (sortedRemovedIndexes.length) {
        queryParams.set(REMOVED_INDEXES_PARAM, sortedRemovedIndexes.join(","));
    }

    const queryString = queryParams.toString();
    return queryString
        ? `${basePath}/${modelSegment}?${queryString}`
        : `${basePath}/${modelSegment}`;
};

export default function CarComparisonPage({ data, slug, city, isLoggedIn, modelsDetails, isApp }: Props) {
    const isMobile = useIsMobile();
    const router = useRouter();
    const pathname = usePathname();
    const { showLoader, hideLoader } = useGlobalLoader();
    updateAppBar(getWishlistAppbarConfig(city?.name, pathname));
    const searchParams = useSearchParams();
    const removedIndexesParam = searchParams?.get(REMOVED_INDEXES_PARAM) || "";
    const legacyRemovedModelsParam = searchParams?.get(LEGACY_REMOVED_MODELS_PARAM) || "";
    const modelsData = modelsDetails || [];
    const availableVariants = modelsData.map((model: any) => {
        const allVariants = model?.allVariantSlugs?.uniqueSortedVariants || [];
        return allVariants.map((variant: any) => ({
            ...variant,
            modelId: model.id,
        }));
    });

    // Move getSelectedVariants above its usage and ensure it always returns CarVariant[]
    const getSelectedVariants = (availableVariants: any[]): CarVariant[] => {
        if (!availableVariants?.length) return [];
        const selected = availableVariants.map((variantList, index) => {
            const slugFromQuery = searchParams?.get(`variant${index + 1}`);
            const match = variantList.find((variant: { slug: string | null; }) => variant.slug === slugFromQuery);
            return match || null; // fallback logic can be added
        });
        // Optional fallback to first available variant if query match fails
        const cleaned = selected.map((variant, index) =>
            variant || availableVariants[index]?.[0]
        );
        return cleaned.filter(Boolean);
    };

    const getFirstCategoryInDisplayOrder = (sections: ComparisonData[]): string | undefined => {
        if (!sections?.length) return undefined;
        const orderMap = new Map(
            COMPARISON_CATEGORY_ORDER.map((cat, i) => [cat.toLowerCase(), i])
        );
        const sorted = [...sections].sort((a, b) => {
            const indexA =
                orderMap.get(a.category.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
            const indexB =
                orderMap.get(b.category.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
        });
        return sorted[0]?.category;
    };

    const [expanded, setExpanded] = useState<{ [key: string]: boolean }>(() => {
        const firstCategory = getFirstCategoryInDisplayOrder(data);
        return firstCategory ? { [firstCategory]: true } : {};
    });
    const [isOnlyDiff, setIsOnlyDiff] = useState<boolean>(false);
    const [filteredData, setFilteredData] = useState<
        ComparisonData[] | null | undefined
    >(data);
    const [compareModelsList, setCompareModelsList] = useState<
        { make: string; model: string }[]
    >([]);
    const [comfirmationModal, setConfirmationModal] = useState<boolean>(false);
    const [selectedIndexToRemove, setSelectedIndexToRemove] = useState<
        number | null
    >(null);
    const [openMMVModal, setOpenMMVModal] = useState<boolean>(false);
    const [selectedVariants, setSelectedVariants] = useState<(CarVariant | null)[]>(getSelectedVariants(availableVariants));
    const [removedIndexes, setRemovedIndexes] = useState<Set<number>>(() =>
        removedIndexesParam
            ? parseRemovedIndexes(removedIndexesParam)
            : parseLegacyRemovedIndexes(legacyRemovedModelsParam, extractCarSlugs(slug))
    );
    const [isEditIndex, setIsEditIndex] = useState<number | null>(null);

    const cityName = city?.name || DEFAULT_CITY;

    useEffect(() => {
        setFilteredData(data);
    }, [data]);

    useEffect(() => {
        if (slug) {
            const list = getMakeModelPairs(slug);
            setCompareModelsList(list);
        }
    }, [slug]);

    useEffect(() => {
        setRemovedIndexes(
            removedIndexesParam
                ? parseRemovedIndexes(removedIndexesParam)
                : parseLegacyRemovedIndexes(legacyRemovedModelsParam, extractCarSlugs(slug))
        );
    }, [slug, removedIndexesParam, legacyRemovedModelsParam]);

    useEffect(() => {
        const carSlugs = extractCarSlugs(slug);
        const hasAnySlugInQuery = ["variant1", "variant2", "variant3"].some((key) =>
            searchParams?.get(key)
        );
        if (!hasAnySlugInQuery && modelsData?.length) {
            let selected: CarVariant[] = [];
            const allSameSlug =
                carSlugs.length > 1 &&
                carSlugs.every((slug) => slug === carSlugs[0]);
            // if comparison happens between the same model slug
            if (allSameSlug) {
                selected = modelsData.map((model, index) => {
                    if (index === 1) return model?.allVariantSlugs?.baseVariant;
                    return model?.allVariantSlugs?.topVariant;
                }).filter((v): v is CarVariant => v !== undefined);
            } else {
                selected = modelsData
                    .map((model) => model?.allVariantSlugs?.topVariant)
                    .filter((v): v is CarVariant => v !== undefined);
            }
            setSelectedVariants(selected);
        }
    }, [modelsData]);

    const toggleAccordion = (category: string) => {
        setExpanded((prev) => ({
            ...prev,
            [category]: !prev[category],
        }));
    };

    const onAddClick = () => {
        setOpenMMVModal(true);
        setIsEditIndex(removedIndexes.size ? Math.min(...removedIndexes) : null);
    };

    const onRemoveClick = (indexToRemove: number) => {
        setSelectedIndexToRemove(indexToRemove);
        setConfirmationModal(true);
    };
    useEffect(() => {
        hideLoader();
    }, [modelsData]);
    const handleVariantChange = (index: number, variant: CarVariant) => {
        showLoader();
        const updated = [...selectedVariants];
        updated[index] = variant;
        setSelectedVariants(updated);

        const newUrl = buildCompareUrlWithRemovedIndexes(
            extractCarSlugs(slug).map((modelSlug, itemIndex) => ({
                modelSlug,
                variantSlug: updated[itemIndex]?.slug,
            })),
            removedIndexes
        );

        router.replace(newUrl);
    };

    const onEdit = (index: number) => {
        setIsEditIndex(index);
        setOpenMMVModal(true);
    }

    const confirmRemove = () => {
        if (selectedIndexToRemove === null) return;

        const nextRemovedIndexes = new Set(removedIndexes);
        nextRemovedIndexes.add(selectedIndexToRemove);
        setSelectedVariants((prev) => {
            const next = [...prev];
            next[selectedIndexToRemove] = null;
            return next;
        });
        setRemovedIndexes(nextRemovedIndexes);

        const nextUrl = buildCompareUrlWithRemovedIndexes(
            extractCarSlugs(slug).map((modelSlug, index) => ({
                modelSlug,
                variantSlug: index === selectedIndexToRemove
                    ? undefined
                    : selectedVariants[index]?.slug,
            })),
            nextRemovedIndexes
        );
        router.replace(nextUrl);
        setConfirmationModal(false);
        setSelectedIndexToRemove(null);
    };

    const cancelRemove = () => {
        setConfirmationModal(false);
        setSelectedIndexToRemove(null);
    };

    const visibleIndexes = useMemo(() => {
        const activeIndexes = modelsData
            .map((_, index) => index)
            .filter((index) => !removedIndexes.has(index));
        const placeholderIndexes = modelsData
            .map((_, index) => index)
            .filter((index) => removedIndexes.has(index))
            .slice(0, Math.max(0, 2 - activeIndexes.length));

        return [...activeIndexes, ...placeholderIndexes].sort((a, b) => a - b);
    }, [modelsData, removedIndexes]);

    const activeHeaderModels = useMemo(
        () => modelsData.filter((_, index) => !removedIndexes.has(index)),
        [modelsData, removedIndexes]
    );

    const sortedFilteredData = useMemo(() => {
        if (!filteredData?.length) return filteredData ?? [];
        const orderMap = new Map(
            COMPARISON_CATEGORY_ORDER.map((cat, i) => [cat.toLowerCase(), i])
        );

        return [...filteredData]
          .map((section) => ({
            ...section,
            features: section.features.map((feature) => ({
              ...feature,
              values: Object.fromEntries(
                Object.entries(feature.values)
                  .map(([key, value], index) => ({ key, value, index }))
                  .filter(({ index }) => visibleIndexes.includes(index))
                  .map(({ key, value, index }) => [
                      key,
                      removedIndexes.has(index) ? "-" : value,
                  ])
              ),
            })),
          }))
          .sort((a, b) => {
            const indexA =
                orderMap.get(a.category.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
            const indexB =
                orderMap.get(b.category.toLowerCase()) ?? Number.MAX_SAFE_INTEGER;
            return indexA - indexB;
          });
    }, [filteredData, removedIndexes, visibleIndexes]);

    const handleDiffFilter = (value: boolean) => {
        setIsOnlyDiff(value);

        if (!value) {
            // Show all data
            setFilteredData(data);
            const firstCategory = getFirstCategoryInDisplayOrder(data);
            setExpanded(firstCategory ? { [firstCategory]: true } : {});
        } else {
            // Filter to only categories and features that have differences
            const filtered: ComparisonData[] = data
                .map((category): ComparisonData | null => {
                    const filteredFeatures = category.features.filter(f => f.hasDifference);
                    if (filteredFeatures.length > 0) {
                        return { ...category, features: filteredFeatures };
                    }
                    return null;
                })
                .filter((c): c is ComparisonData => c !== null);

            setFilteredData(filtered);
            const firstFilteredCategory = getFirstCategoryInDisplayOrder(filtered);
            setExpanded(firstFilteredCategory ? { [firstFilteredCategory]: true } : {});
        }
    };

    const handleMMVSelection = (
        brand: string,
        modelSlug: string,
        variantSlug: string,
        selectedVariant?: CarVariant | any
    ) => {
        showLoader();
        let newSlug = slug;
        const updatedVariants = [...selectedVariants];
        const nextRemovedIndexes = new Set(removedIndexes);

        if (isEditIndex != null) {
            // Replace model in slug
            newSlug = updateComparisonSlug(slug, isEditIndex, modelSlug);
            nextRemovedIndexes.delete(isEditIndex);

            // Update the selected variant at the edited index
            updatedVariants[isEditIndex] = selectedVariant || ({ slug: variantSlug } as CarVariant);
        } else {
            // Add new model to slug
            newSlug = addModelToComparisonSlug(slug, modelSlug);
            updatedVariants.push(selectedVariant || ({ slug: variantSlug } as CarVariant));
        }

        setRemovedIndexes(nextRemovedIndexes);
        router.push(
            buildCompareUrlWithRemovedIndexes(
                extractCarSlugs(newSlug).map((modelSlug, index) => ({
                    modelSlug,
                    variantSlug: updatedVariants[index]?.slug,
                })),
                nextRemovedIndexes
            )
        );
    };

    const closeConfirmationModal = () => {
        showLoader();
        setConfirmationModal(false);
    };
    return (
        <>
            <ModalManager
                modalHeaderClass={styles.modalHeader}
                isOpen={!!comfirmationModal}
                onClose={closeConfirmationModal }
                title={
                    <div className={styles.modalTitleWrapper}>
                        <ImageAtom src={`${assetLoader()}/triangle-warning.png`} alt="triangle" height={24} width={24} />
                    </div>
                }
            >
                <div className={styles.modalContentWrapper}>
                    <div className={styles.modalTitle}>Are you sure you want to remove this variant?</div>
                    <div className={styles.modalActions}>
                        <Button className={styles.modalPrimaryBtn} size={"lg"} onButtonClick={cancelRemove}>Don&apos;t remove</Button>
                        <Button className={styles.modalSecondaryBtn} size={"lg"} onButtonClick={confirmRemove}>Yes, Remove</Button>
                    </div>
                </div>
            </ModalManager>
            <AddToCompareModal isOpen={openMMVModal} onClose={() => setOpenMMVModal(false)} title={'Add a car to compare'} cityName={cityName} handleAddToCompare={handleMMVSelection} />
            {compareModelsList && availableVariants?.length > 0 && modelsData?.length > 0 && <div className={styles.comparisonWrapper}>
                <div className={styles.header}>
                    <div className={styles.titleSection}>
                        <h1 className={`${styles.subTitle} ${isApp && styles.subTitleApp }`}>
                            {isApp ? "Compare cars" : activeHeaderModels.map((item, index) => (
                                <Fragment key={`${item.model.slug}-${index}`}>
                                    {index > 0 && <strong className={styles.vsSeparator}> VS </strong>}
                                    <span>{item.brand.name} {item.model.name}</span>
                                </Fragment>
                            ))}
                        </h1>
                    </div>
                    <div className={styles.headerActions}>
                        {!isMobile && <div className={styles.diffSwitchToggle}>
                            <ToggleSwitch
                                isOn={isOnlyDiff}
                                onToggle={handleDiffFilter}
                            />
                            Show only the differences
                        </div>}
                        {modelsData.length - removedIndexes.size < MAX_COMPARISON_CARDS && (
                            <button className={styles.addButton} onClick={onAddClick}>
                                <ImageAtom
                                    src={`${assetLoader()}/icons/Add_darker.svg`}
                                    alt="Add"
                                    width={12}
                                    height={12}
                                    className={styles.icon}
                                />
                               {isMobile ? 'Add' : 'Add car'}
                            </button>
                        )}
                    </div>
                </div>
                <div className={styles.compareCardsWrapper}>
                    {visibleIndexes.map((index) => {
                        const item = modelsData[index];
                        return (
                        <CompareCard
                            key={index}
                            make={item.brand.name}
                            model={item.model.name}
                            image={`${s3UrlLoader()}/${toKebabCase(item.brand.name)}/${toKebabCase(item.model.name)}/default/Hero-Transparent.png`}
                            variants={availableVariants[index]}
                            selectedVariant={selectedVariants[index] || undefined}
                            onVariantChange={(newVariant) => handleVariantChange(index, newVariant)}
                            onRemove={() => onRemoveClick(index)}
                            onEdit={() => onEdit(index)}
                            index={index}
                            slug={item.model.slug}
                            isSellable={item.status !== "out_of_production" && item.status !== "non_sellable"}
                            isApp={isApp}
                            isEmpty={removedIndexes.has(index)}
                        />
                        );
                    })}
                </div>
                {isMobile && <div className={styles.diffSwitchToggle}>
                    <ToggleSwitch
                        isOn={isOnlyDiff}
                        onToggle={handleDiffFilter}
                    />
                    Show only the differences
                </div>}
                <div className={styles.priceBlock}>
                    {visibleIndexes.map((index) => {
                        const variant = removedIndexes.has(index)
                            ? null
                            : selectedVariants[index];
                        return variant ? (
                            <ComparePriceCard
                                key={index}
                                adspPrice={variant.adspPrice || 0}
                                onRoadPrice={variant.onRoadPrice || 0}
                                ackoDriveSavings={variant.ackoDriveSavings || 0}
                                cityName={cityName}
                                isLoggedIn={isLoggedIn}
                                isMobile={isMobile}
                            />
                        ) : (
                            <div key={index} className={styles.emptyPriceCard}>
                                <div>ACKO Drive price in {cityName}</div>
                                <strong>-</strong>
                            </div>
                        );
                    })}
                </div>
                <div className={styles.accordionContainer}>
                    {sortedFilteredData?.map((section) => (
                        <div key={section.category} className={styles.accordion}>
                            <div
                                className={styles.accordionHeader}
                                onClick={() => toggleAccordion(section.category)}
                            >
                                <AccentBar isActive={expanded[section.category]} />
                                <h2 className={styles.accordionCategory}>{section.category}</h2>
                                <ImageAtom
                                    src={`${assetLoader()}${expanded[section.category] ? `/up-arrow.svg` : `/down-arrow.svg`}`}
                                    alt={expanded[section.category] ? "Collapse" : "Expand"}
                                    width={24}
                                    height={24}
                                />
                            </div>
                            {expanded[section.category] && (
                                <div className={styles.accordionContent}>
                                    <div className={styles.featureBlock}>
                                        {section.features.map((feature) => (
                                            <div className={styles.featureRow} key={feature.feature}>
                                                <div className={styles.featureTitle}>{feature.feature}</div>
                                                <div className={styles.featureValueContainer}>
                                                    {Object.entries(feature.values).map(([variant, value]) => {
                                                        return (
                                                          <div
                                                            key={variant}
                                                            className={
                                                              styles.featureValue
                                                            }
                                                          >
                                                            {!value ||
                                                            (typeof value ===
                                                              "string" &&
                                                              value.trim()
                                                                .length === 0)
                                                              ? "-"
                                                              : value}
                                                          </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>}
        </>
    );
}
